/**
 * Client-side stack-trace deobfuscation for Cloud Error Reporting.
 *
 * Why this exists: Cloud Error Reporting has no native source-map
 * upload step (unlike Sentry). To get readable stack traces in prod,
 * we ship `.map` files alongside the JS chunks to Firebase Hosting,
 * then fetch + parse them on demand the first time an error fires.
 * The cost is paid only when a real error happens — `source-map-js`
 * is dynamically imported and source maps are downloaded lazily.
 *
 * Trade-offs:
 *   - Source maps are public (anyone who reads the JS can grab them
 *     too). For an SPA where the JS is already client-side, this is
 *     fine — Sentry doesn't actually protect IP either, it just hides
 *     the maps behind an auth token. The user can still walk the
 *     bundle with browser devtools.
 *   - First error in a session pays the map-fetch latency
 *     (~50–200ms per unique chunk). Subsequent errors hit the cache.
 *   - When source-map-js fails or the .map is missing, we transparently
 *     fall back to the original stack so no error is ever lost.
 *
 * Stack trace shapes handled:
 *   - V8/Chrome:  `    at functionName (https://host/assets/chunk-abc.js:123:45)`
 *   - V8 anon:    `    at https://host/assets/chunk-abc.js:123:45`
 *   - V8 eval:    `    at functionName (eval at <anonymous>, https://host/assets/chunk-abc.js:123:45)`
 *   - Safari/FF:  `functionName@https://host/assets/chunk-abc.js:123:45`
 */

import type { RawSourceMap } from "source-map-js";

// One in-flight promise per `.map` URL — coalesces duplicate fetches
// when many simultaneous errors hit the same chunk on the first frame.
const fetchInFlight = new Map<string, Promise<RawSourceMap | null>>();
// Cached parsed consumers. The library's `SourceMapConsumer` shape is
// dynamic; we type it as `unknown` and narrow when used.
const consumerCache = new Map<string, unknown>();

interface SymbolicateOptions {
  /** Max time to spend symbolicating before giving up and returning
   *  the raw stack. Keeps a slow CDN from blocking the error report.
   *  Defaults to 1500ms. */
  timeoutMs?: number;
}

/**
 * Rewrite a stack trace string with original source positions where
 * we can resolve them via `.map` files. Returns the rewritten stack,
 * or the original on any failure.
 *
 * Idempotent — passing an already-symbolicated stack is a no-op (we
 * only rewrite lines that match the bundled-asset pattern).
 */
export async function symbolicate(
  stack: string,
  options: SymbolicateOptions = {}
): Promise<string> {
  if (!stack) return stack;
  const { timeoutMs = 1500 } = options;

  try {
    const lines = stack.split("\n");
    const symbolicated = await Promise.race([
      symbolicateLines(lines),
      new Promise<string[]>((resolve) =>
        setTimeout(() => resolve(lines), timeoutMs)
      ),
    ]);
    return symbolicated.join("\n");
  } catch {
    // Any unexpected failure → return raw stack so the error STILL
    // gets reported, just without source-mapped frames.
    return stack;
  }
}

/** Per-line rewrite. Lines we can't parse pass through unchanged. */
async function symbolicateLines(lines: string[]): Promise<string[]> {
  // Resolve frame-by-frame in sequence (not parallel) so the cache
  // populates between adjacent frames on the same chunk. The total
  // network cost is `O(unique chunks in stack)`, usually 1–3.
  const out: string[] = [];
  for (const line of lines) {
    out.push(await symbolicateLine(line));
  }
  return out;
}

interface ParsedFrame {
  /** Display name to keep in the rewritten frame (or null). */
  fn: string | null;
  /** Absolute or relative URL of the bundled chunk. */
  url: string;
  line: number;
  column: number;
  /** Index of the original `(url:line:col)` triplet within `line` so
   *  we can splice the rewrite back in without disturbing the rest. */
  matchStart: number;
  matchEnd: number;
}

/** Try V8 + Safari/Firefox shapes. Returns null if neither matches. */
function parseFrame(line: string): ParsedFrame | null {
  // V8: "    at fn (url:line:col)" or "    at url:line:col"
  // The capturing group covers `url:line:col` so we can splice.
  const v8WithFn = /^(\s*at\s+)([^(]+?)\s+\((.+):(\d+):(\d+)\)\s*$/.exec(line);
  if (v8WithFn) {
    const [, , fn, url, ln, col] = v8WithFn;
    // matchStart/matchEnd inside the original line for the (url:l:c) triplet.
    const matchStart = line.indexOf("(");
    const matchEnd = line.lastIndexOf(")");
    return {
      fn: fn.trim(),
      url,
      line: Number(ln),
      column: Number(col),
      matchStart,
      matchEnd: matchEnd + 1,
    };
  }
  const v8AnonOrTopLevel = /^(\s*at\s+)(.+):(\d+):(\d+)\s*$/.exec(line);
  if (v8AnonOrTopLevel) {
    const [, prefix, url, ln, col] = v8AnonOrTopLevel;
    return {
      fn: null,
      url,
      line: Number(ln),
      column: Number(col),
      matchStart: prefix.length,
      matchEnd: line.length,
    };
  }
  // Safari/Firefox: "fn@url:line:col"
  const spider = /^(.*?)@(.+):(\d+):(\d+)\s*$/.exec(line);
  if (spider) {
    const [, fn, url, ln, col] = spider;
    return {
      fn: fn || null,
      url,
      line: Number(ln),
      column: Number(col),
      matchStart: 0,
      matchEnd: line.length,
    };
  }
  return null;
}

async function symbolicateLine(line: string): Promise<string> {
  const frame = parseFrame(line);
  if (!frame) return line;

  // Only attempt symbolication for URLs that look like our own bundled
  // assets. Cross-origin script frames (e.g. third-party CDNs) won't
  // have a fetchable `.map`, and we don't want to spam network requests
  // chasing them.
  if (!isOwnAsset(frame.url)) return line;

  const mapUrl = `${frame.url}.map`;
  const consumer = await getConsumer(mapUrl);
  if (!consumer) return line;

  try {
    // The library's `originalPositionFor` is on the consumer instance;
    // we type-narrow via duck-typing to avoid leaking source-map-js
    // types into the calling code that's purely runtime.
    type OriginalPositionFn = (pos: { line: number; column: number }) => {
      source: string | null;
      line: number | null;
      column: number | null;
      name: string | null;
    };
    const fn = (consumer as { originalPositionFor: OriginalPositionFn })
      .originalPositionFor;
    const orig = fn({ line: frame.line, column: frame.column });
    if (!orig.source || orig.line == null) return line;

    const displayName = orig.name ?? frame.fn ?? "<anonymous>";
    const sourceLoc = `${orig.source}:${orig.line}:${orig.column ?? 0}`;
    // Preserve the original prefix (`    at `) and any trailing text so
    // multi-frame stacks remain visually consistent.
    const prefix = line.slice(0, frame.matchStart);
    const suffix = line.slice(frame.matchEnd);
    // V8-style rewrite — Error Reporting parses both V8 and Spider
    // formats but V8 is the canonical one.
    return `${prefix.trimEnd()} ${displayName} (${sourceLoc})${suffix}`;
  } catch {
    return line;
  }
}

/** Same-origin assets under our bundle path. */
function isOwnAsset(url: string): boolean {
  if (typeof window === "undefined") return false;
  if (url.startsWith("/")) return true;
  try {
    const parsed = new URL(url);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

async function getConsumer(mapUrl: string): Promise<unknown | null> {
  const cached = consumerCache.get(mapUrl);
  if (cached) return cached;
  const raw = await fetchMap(mapUrl);
  if (!raw) return null;
  try {
    // Lazy import — `source-map-js` is only loaded once the first
    // error fires. Keeps the happy-path bundle slim.
    const { SourceMapConsumer } = await import("source-map-js");
    const consumer = new SourceMapConsumer(raw);
    consumerCache.set(mapUrl, consumer);
    return consumer;
  } catch {
    return null;
  }
}

async function fetchMap(mapUrl: string): Promise<RawSourceMap | null> {
  const existing = fetchInFlight.get(mapUrl);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const res = await fetch(mapUrl, { credentials: "omit", cache: "force-cache" });
      if (!res.ok) return null;
      return (await res.json()) as RawSourceMap;
    } catch {
      return null;
    } finally {
      // Tiny race: an awaiting caller might still hold the promise
      // after we drop it from the map. That's fine — they get the
      // resolved value (or null), and subsequent callers either hit
      // the consumer cache (success) or re-fetch (cheap on HTTP/2).
      fetchInFlight.delete(mapUrl);
    }
  })();
  fetchInFlight.set(mapUrl, promise);
  return promise;
}

/** Test-only: wipe caches so tests start clean. */
export function _resetSymbolicateForTests(): void {
  fetchInFlight.clear();
  consumerCache.clear();
}
